import React from 'react';
import type { TooltipContentProps } from 'recharts';
import { clsx } from 'clsx';
import { Pie, PieChart, ResponsiveContainer, Tooltip as RechartTooltip } from 'recharts';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

const COLOR_RESOLVED = '#5D4EC7';
const COLOR_ESCALATED = '#E1DCFF';

type ResolutionRateCardProps = {
  resolved?: number;
  escalated?: number;
  trend?: number | null;
  tooltipDescription?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  title?: string;
  showRate?: boolean;
};

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={'info-tooltip-icon'}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Zm0-1.666a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Zm.833-10a.833.833 0 1 1-1.666 0 .833.833 0 0 1 1.666 0ZM9.167 9.167h1.666v5h-1.666v-5Z"
      fill="#8585A0"
    />
  </svg>
);

const TrendUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    aria-hidden="true"
  >
    <path d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z" fill="#12823B" />
  </svg>
);

const TrendDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    aria-hidden="true"
  >
    <path d="M8.5 12L4.60289 5.25H12.3971L8.5 12Z" fill="#C11A45" />
  </svg>
);

const CustomTooltip = ({
  active = false,
  payload = [],
}: Partial<TooltipContentProps<number, string>>) => {
  if (active && payload?.length) {
    return (
      <div
        className={
          'bg-white rounded-sm-5 shadow-tooltip text-xs py-1.5 px-3 flex flex-row gap-2 items-center font-jamjuree'
        }
      >
        <div className={'flex items-center gap-0.5'}>
          <p className="font-medium">{`${payload[0].name} -`}</p>{' '}
          <p className="font-bold text-sm">{`${payload[0].value}`}</p>
        </div>
      </div>
    );
  }

  return null;
};

export const ResolutionRateCard: React.FC<ResolutionRateCardProps> = ({
  resolved = 0,
  escalated = 0,
  trend = null,
  tooltipDescription,
  icon,
  className,
  title = 'Resolution Rate',
  showRate = true,
}) => {
  const total = resolved + escalated;
  const rate = total > 0 ? Math.round((resolved / total) * 100) : 0;

  const data =
    total > 0
      ? [
          { name: 'Resolved', value: resolved, fill: COLOR_RESOLVED },
          { name: 'Escalated', value: escalated, fill: COLOR_ESCALATED },
        ]
      : [{ name: 'Empty', value: 1, fill: '#F3F4F7' }];

  const hasTrend = typeof trend === 'number' && trend !== 0;
  const isTrendUp = (trend ?? 0) > 0;

  return (
    <div
      className={clsx(
        'flex flex-col items-center gap-4 p-4 w-full bg-white border border-[#F3F4F7] rounded-sm-5 font-jamjuree',
        className,
      )}
    >
      <div className="flex flex-col gap-1 items-start w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium leading-[1.5] text-[#2A2A3A]">{title}</span>
            {tooltipDescription && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="cursor-pointer flex" aria-label="More info">
                      <InfoIcon />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs text-tertiary font-medium">
                    {tooltipDescription}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {icon}
        </div>
        {showRate && (
          <div className="flex items-center gap-1 h-8">
            <span className="text-[28px] font-bold leading-[35px] text-ebony">{rate}%</span>
            {hasTrend && (
              <div className="flex items-center gap-0.5">
                {isTrendUp ? <TrendUpIcon /> : <TrendDownIcon />}
                <span
                  className={clsx(
                    'text-xs font-medium leading-[1.5]',
                    isTrendUp ? 'text-[#12823B]' : 'text-[#C11A45]',
                  )}
                >
                  {Math.abs(trend as number)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center justify-center flex-1 gap-4 w-[270px]">
        <div className="w-full h-[270px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={85}
                outerRadius={125}
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                isAnimationActive={total > 0}
                stroke="none"
              />
              <RechartTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-3 w-full">
          <LegendItem color={COLOR_RESOLVED} label="Resolved" />
          <LegendItem color={COLOR_ESCALATED} label="Escalated" />
        </div>
      </div>
    </div>
  );
};

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span
      className="w-3 h-3 rounded-full border-[3px] border-solid"
      style={{ borderColor: color }}
    />
    <span className="text-xs font-medium leading-[1.5] text-tertiary">{label}</span>
  </div>
);
